import { gql } from "@apollo/client";

export const GET_PAYEE_TRANSACTIONS_OVERVIEW = gql`
  query GetPayeeTransactionsOverview(
    $prevPeriodStartDate: date
    $prevPeriodEndDate: date
    $curPeriodStartDate: date
    $curPeriodEndDate: date
  ) {
    prevPeriodAggregate: financial_transaction_log_daily_summary_aggregate(
      where: { date: { _gte: $prevPeriodStartDate, _lte: $prevPeriodEndDate } }
    ) {
      aggregate {
        sum {
          potentialRevenueAmount: potential_revenue
          registeredTransactionsAmount: registered_price
          walletTransactionsAmount: gateway_transaction_price
          tripChargeAmount: trip_charge_price
        }
      }
    }
    # AUT-6163: adjustments (chargebacks/credits) are in no daily-summary bucket, and the log
    # views' price is their one source of truth, so they're summed here per tab they belong to: a
    # park-referenced one nets into the park total, a wallet_reload- or permit-referenced one into the
    # gateway (wallet) total. Same date basis (summary_date) as the daily summary.
    prevPeriodParkAdjustmentsAggregate: financial_transaction_log_no_balance_aggregate(
      where: {
        transaction_type: { _eq: "adjustment" }
        reference_transaction_type: { _eq: "park" }
        summary_date: { _gte: $prevPeriodStartDate, _lte: $prevPeriodEndDate }
      }
    ) {
      aggregate {
        sum {
          price
        }
      }
    }
    curPeriodParkAdjustmentsAggregate: financial_transaction_log_no_balance_aggregate(
      where: {
        transaction_type: { _eq: "adjustment" }
        reference_transaction_type: { _eq: "park" }
        summary_date: { _gte: $curPeriodStartDate, _lte: $curPeriodEndDate }
      }
    ) {
      aggregate {
        sum {
          price
        }
      }
    }
    prevPeriodGatewayAdjustmentsAggregate: financial_transaction_log_no_balance_aggregate(
      where: {
        transaction_type: { _eq: "adjustment" }
        reference_gateway_transaction_type: { _in: ["wallet_reload", "permit"] }
        summary_date: { _gte: $prevPeriodStartDate, _lte: $prevPeriodEndDate }
      }
    ) {
      aggregate {
        sum {
          price
        }
      }
    }
    curPeriodGatewayAdjustmentsAggregate: financial_transaction_log_no_balance_aggregate(
      where: {
        transaction_type: { _eq: "adjustment" }
        reference_gateway_transaction_type: { _in: ["wallet_reload", "permit"] }
        summary_date: { _gte: $curPeriodStartDate, _lte: $curPeriodEndDate }
      }
    ) {
      aggregate {
        sum {
          price
        }
      }
    }
    curPeriodAggregate: financial_transaction_log_daily_summary_aggregate(
      where: { date: { _gte: $curPeriodStartDate, _lte: $curPeriodEndDate } }
    ) {
      aggregate {
        sum {
          potentialRevenueAmount: potential_revenue
          registeredTransactionsAmount: registered_price
          walletTransactionsAmount: gateway_transaction_price
          tripChargeAmount: trip_charge_price
        }
      }
    }
  }
`;

/** Variables passed to the {@link GET_PAYEE_TRANSACTIONS_OVERVIEW} query */
export interface GetPayeeTransactionsOverviewVars {
  /** Inclusive start date of the previous period expressed as a string with the format yyyy-mm-dd */
  prevPeriodStartDate: string;
  /** Inclusive end date of the previous period expressed as a string with the format yyyy-mm-dd */
  prevPeriodEndDate: string;
  /** Inclusive start date of the current period expressed as a string with the format yyyy-mm-dd */
  curPeriodStartDate: string;
  /** Inclusive end date of the current period expressed as a string with the format yyyy-mm-dd */
  curPeriodEndDate: string;
}

export interface PayeeTransactionsSummaryAggregate {
  aggregate: {
    sum: {
      potentialRevenueAmount: number;
      registeredTransactionsAmount: number;
      walletTransactionsAmount: number;
      tripChargeAmount: number;
    };
  };
}

/** Sum of the wallet effect (`price`) of one tab's adjustments (AUT-6163) in a period. */
export interface PayeeAdjustmentsAggregate {
  aggregate: {
    sum: {
      price: number | null;
    };
  };
}

export interface GetPayeeTransactionsOverviewData {
  /** Sum totals in the previous period */
  prevPeriodAggregate: PayeeTransactionsSummaryAggregate;
  /** Sum totals in the current period */
  curPeriodAggregate: PayeeTransactionsSummaryAggregate;
  /** Park-referenced adjustments in the previous period */
  prevPeriodParkAdjustmentsAggregate: PayeeAdjustmentsAggregate;
  /** Park-referenced adjustments in the current period */
  curPeriodParkAdjustmentsAggregate: PayeeAdjustmentsAggregate;
  /** wallet_reload- and permit-referenced adjustments in the previous period */
  prevPeriodGatewayAdjustmentsAggregate: PayeeAdjustmentsAggregate;
  /** wallet_reload- and permit-referenced adjustments in the current period */
  curPeriodGatewayAdjustmentsAggregate: PayeeAdjustmentsAggregate;
}
